import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { cn } from '@/components/ui/utils';

/**
 * Section wrapper used all over /app — gives a header row with title,
 * optional subtitle, and an optional "View all" link. Keeps spacing
 * consistent across Home, Drafts, Orders, etc.
 */
export interface SectionCardProps {
  title: string;
  subtitle?: string;
  viewAllTo?: string;
  viewAllLabel?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** When true, removes the Card wrapper — content renders flush (used on list pages). */
  flush?: boolean;
}

export function SectionCard({
  title,
  subtitle,
  viewAllTo,
  viewAllLabel = 'View all',
  action,
  children,
  className,
  flush = false,
}: SectionCardProps) {
  const header = (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h2 className="font-semibold text-[15px] text-[var(--text)] leading-tight">{title}</h2>
        {subtitle ? (
          <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">{subtitle}</p>
        ) : null}
      </div>
      {viewAllTo ? (
        <Link
          to={viewAllTo}
          className="shrink-0 text-[13px] font-medium text-[var(--brand)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] rounded"
        >
          {viewAllLabel}
        </Link>
      ) : action ?? null}
    </div>
  );

  if (flush) {
    return (
      <section className={className} aria-labelledby={undefined}>
        {header}
        {children}
      </section>
    );
  }

  return (
    <section className={className}>
      {header}
      <Card className={cn('p-0 overflow-hidden')}>{children}</Card>
    </section>
  );
}
